import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Send, Trophy, MapPin, Users, ShieldCheck, Mail, KeyRound, MessageSquare, Info, Copy, Check, Lock } from "lucide-react";
import { Section, SectionTitle, Reveal } from "../components/UIBits";
import { TOURNAMENT, CATEGORY_YEARS, categoryLabel } from "../lib/constants";
import { useT } from "../lib/i18n";
import api, { formatErr } from "../lib/api";

const INTERAC_EMAIL = "defiultimedek@gmail.com";
const INTERAC_ANSWER = "hockey";

export default function InscriptionPage() {
    const { t } = useT();
    const nav = useNavigate();
    const [form, setForm] = useState({
        team_name: "",
        category: TOURNAMENT.categories[0],
        city: "",
        manager_name: "",
        email: "",
        phone: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState("");
    const [registrationsOpen, setRegistrationsOpen] = useState(true);
    const [settingsLoaded, setSettingsLoaded] = useState(false);

    useEffect(() => {
        api.get("/settings")
            .then(r => setRegistrationsOpen(r.data.registrations_open !== false))
            .catch(() => setRegistrationsOpen(true))
            .finally(() => setSettingsLoaded(true));
    }, []);

    const copyToClipboard = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(label);
            setTimeout(() => setCopied(""), 2000);
        } catch (e) {
            const el = document.createElement("textarea");
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            setCopied(label);
            setTimeout(() => setCopied(""), 2000);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/registrations", form);
            nav(`/inscription/succes?id=${data.id}`, { state: { registration: data } });
        } catch (ex) {
            setErr(formatErr(ex));
            setBusy(false);
        }
    };

    const placeholderMsg = `(${t("inscription.team_name").toLowerCase()}) - (${t("inscription.category").toLowerCase()})`;
    const interacMessage = form.team_name && form.category ? `${form.team_name} - ${form.category}` : placeholderMsg;

    return (
        <div data-testid="inscription-page">
            <Helmet><title>{`${t("inscription.title_pre")} ${t("inscription.title_accent")} — Défi Ultime`}</title></Helmet>
            <Section>
                <SectionTitle kicker={t("inscription.kicker")} title={t("inscription.title_pre")} accent={t("inscription.title_accent")} />
                {settingsLoaded && !registrationsOpen ? (
                    <Reveal>
                        <div className="glass-card p-8 sm:p-12 text-center border-l-4 border-rose-400 max-w-2xl mx-auto" data-testid="registrations-closed">
                            <Lock size={48} className="mx-auto mb-6 text-rose-400" />
                            <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-widest mb-4">{t("inscription.closed_title")}</h2>
                            <p className="text-white/70 leading-relaxed max-w-md mx-auto">{t("inscription.closed_body")}</p>
                        </div>
                    </Reveal>
                ) : (
                <div className="grid lg:grid-cols-5 gap-8">
                    <Reveal className="lg:col-span-2 order-2 lg:order-1">
                        <div className="glass-card p-6 mb-4 border-l-4 border-[#7FD93C]">
                            <div className="label-mini mb-2">{t("inscription.fee_label")}</div>
                            <div className="font-display text-6xl text-[#7FD93C]">{TOURNAMENT.registrationFee} $</div>
                            <div className="text-sm text-white/60 mt-1">{TOURNAMENT.currency} • {t("inscription.fee_sub")}</div>
                        </div>

                        <div className="glass-card p-6 mb-4 border-l-4 border-[#4FE0D5]" data-testid="interac-info">
                            <div className="flex items-center gap-2 mb-3">
                                <Send className="text-[#4FE0D5]" size={18} />
                                <h3 className="font-display text-xl uppercase tracking-widest text-[#4FE0D5]">{t("inscription.interac_title")}</h3>
                            </div>
                            <p className="text-sm text-white/70 mb-4">{t("inscription.interac_lead")}</p>
                            <div className="grid gap-3">
                                <button type="button" onClick={() => copyToClipboard(INTERAC_EMAIL, "email")} className="w-full text-left flex items-center gap-3 bg-black/30 hover:bg-black/50 active:bg-[#7FD93C]/15 p-3 transition-colors group cursor-pointer border-l-2 border-transparent hover:border-[#7FD93C]" data-testid="btn-copy-interac-email">
                                    <Mail size={18} className="text-[#7FD93C] flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="label-mini mb-1">{t("inscription.interac_email_label")}</div>
                                        <div className="font-mono text-sm break-all">{INTERAC_EMAIL}</div>
                                    </div>
                                    <span className={`flex-shrink-0 px-2.5 py-1 angle-tag border font-display text-[11px] tracking-widest uppercase flex items-center gap-1 transition-colors ${copied === "email" ? "bg-[#7FD93C] border-[#7FD93C] text-black" : "bg-[#7FD93C]/15 border-[#7FD93C]/40 text-[#7FD93C] group-hover:bg-[#7FD93C] group-hover:text-black"}`}>
                                        {copied === "email" ? <><Check size={12} /> {t("common.copied")}</> : <><Copy size={12} /> {t("common.copy")}</>}
                                    </span>
                                </button>
                                <button type="button" onClick={() => copyToClipboard(INTERAC_ANSWER, "answer")} className="w-full text-left flex items-center gap-3 bg-black/30 hover:bg-black/50 active:bg-[#7FD93C]/15 p-3 transition-colors group cursor-pointer border-l-2 border-transparent hover:border-[#7FD93C]" data-testid="btn-copy-interac-answer">
                                    <KeyRound size={18} className="text-[#7FD93C] flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="label-mini mb-1">{t("inscription.interac_answer_label")}</div>
                                        <div className="font-mono text-sm">{INTERAC_ANSWER}</div>
                                    </div>
                                    <span className={`flex-shrink-0 px-2.5 py-1 angle-tag border font-display text-[11px] tracking-widest uppercase flex items-center gap-1 transition-colors ${copied === "answer" ? "bg-[#7FD93C] border-[#7FD93C] text-black" : "bg-[#7FD93C]/15 border-[#7FD93C]/40 text-[#7FD93C] group-hover:bg-[#7FD93C] group-hover:text-black"}`}>
                                        {copied === "answer" ? <><Check size={12} /> {t("common.copied")}</> : <><Copy size={12} /> {t("common.copy")}</>}
                                    </span>
                                </button>
                                <button type="button" onClick={() => form.team_name && copyToClipboard(interacMessage, "message")} disabled={!form.team_name} className="w-full text-left flex items-start gap-3 bg-black/30 hover:bg-black/50 active:bg-[#7FD93C]/15 p-3 transition-colors group cursor-pointer border-l-2 border-transparent hover:border-[#7FD93C] disabled:cursor-default disabled:hover:bg-black/30 disabled:hover:border-transparent" data-testid="btn-copy-interac-message">
                                    <MessageSquare size={18} className="text-[#7FD93C] mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="label-mini mb-1">{t("inscription.interac_message_label")} {form.team_name && `· ${t("common.tap_to_copy")}`}</div>
                                        <div className="font-mono text-sm break-words" data-testid="interac-message-preview">{interacMessage}</div>
                                        <div className="text-[10px] text-white/40 mt-1">{t("inscription.interac_message_format")}</div>
                                    </div>
                                    {form.team_name && (
                                        <span className={`flex-shrink-0 self-center px-2.5 py-1 angle-tag border font-display text-[11px] tracking-widest uppercase flex items-center gap-1 transition-colors ${copied === "message" ? "bg-[#7FD93C] border-[#7FD93C] text-black" : "bg-[#7FD93C]/15 border-[#7FD93C]/40 text-[#7FD93C] group-hover:bg-[#7FD93C] group-hover:text-black"}`}>
                                            {copied === "message" ? <><Check size={12} /> {t("common.copied")}</> : <><Copy size={12} /> {t("common.copy")}</>}
                                        </span>
                                    )}
                                </button>
                                <div className="flex items-start gap-3 bg-[#7FD93C]/10 p-3 border-l-2 border-[#7FD93C]">
                                    <ShieldCheck size={16} className="text-[#7FD93C] mt-0.5 flex-shrink-0" />
                                    <div className="text-xs text-white/80">{t("inscription.interac_safe")}</div>
                                </div>
                            </div>
                        </div>

                        <div className="glass-card p-6 grid gap-4">
                            <div className="flex items-start gap-3">
                                <Trophy className="text-[#4FE0D5] mt-1 flex-shrink-0" size={20} />
                                <div className="min-w-0 flex-1">
                                    <div className="font-display text-lg uppercase tracking-wide">{t("inscription.categories_avail")}</div>
                                    <div className="grid gap-1 mt-1 text-sm">
                                        {TOURNAMENT.categories.map(c => (
                                            <div key={c} className="flex justify-between gap-3">
                                                <span className="text-white/80 font-semibold">{c}</span>
                                                <span className="text-[#4FE0D5] font-mono text-xs">{CATEGORY_YEARS[c]}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <MapPin className="text-[#4FE0D5] mt-1 flex-shrink-0" size={20} />
                                <div>
                                    <div className="font-display text-lg uppercase tracking-wide">{t("inscription.location")}</div>
                                    <div className="text-sm text-white/70">{TOURNAMENT.arena} — {t("tournament.date")}</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Users className="text-[#4FE0D5] mt-1 flex-shrink-0" size={20} />
                                <div>
                                    <div className="font-display text-lg uppercase tracking-wide">{t("inscription.lineup")}</div>
                                    <div className="text-sm text-white/70">{t("inscription.lineup_max")}</div>
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1} className="lg:col-span-3 order-1 lg:order-2">
                        <form onSubmit={submit} className="glass-card p-6 grid gap-4" data-testid="inscription-form">
                            <div className="flex items-center gap-2 text-white/80 mb-2">
                                <Info size={16} className="text-[#4FE0D5]" />
                                <span className="text-sm">{t("inscription.form_lead")}</span>
                            </div>

                            <div>
                                <label className="label-mini block mb-1">{t("inscription.team_name")}</label>
                                <input data-testid="inp-team-name" required className="input-field" value={form.team_name} onChange={e => setForm({ ...form, team_name: e.target.value })} placeholder={t("inscription.team_name_placeholder")} />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-mini block mb-1">{t("inscription.category")}</label>
                                    <select data-testid="inp-category" required className="input-field" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                                        {TOURNAMENT.categories.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                                    </select>
                                    <div className="text-[10px] text-white/50 mt-1">{t("inscription.birth_year")} : {CATEGORY_YEARS[form.category]}</div>
                                </div>
                                <div>
                                    <label className="label-mini block mb-1">{t("inscription.city")}</label>
                                    <input data-testid="inp-city" required className="input-field" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder={t("inscription.city_placeholder")} />
                                </div>
                            </div>
                            <div>
                                <label className="label-mini block mb-1">{t("inscription.manager")}</label>
                                <input data-testid="inp-manager" required className="input-field" value={form.manager_name} onChange={e => setForm({ ...form, manager_name: e.target.value })} />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-mini block mb-1">{t("inscription.email")}</label>
                                    <input data-testid="inp-email" required type="email" className="input-field" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                                </div>
                                <div>
                                    <label className="label-mini block mb-1">{t("inscription.phone")}</label>
                                    <input data-testid="inp-phone" required className="input-field" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="514-555-0000" />
                                </div>
                            </div>

                            <div className="border-t border-white/10 pt-4 flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <div className="label-mini">{t("inscription.total")}</div>
                                    <div className="font-display text-3xl text-[#7FD93C]">{TOURNAMENT.registrationFee} $ <span className="text-base text-white/60">{TOURNAMENT.currency}</span></div>
                                </div>
                                <motion.button data-testid="btn-submit-registration" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" disabled={busy} className="btn-primary">
                                    <Send size={16} /> {busy ? t("inscription.submitting") : t("inscription.submit")}
                                </motion.button>
                            </div>
                            {err && <div className="text-rose-400 text-sm" data-testid="inscription-error">{err}</div>}
                            <div className="text-xs text-white/40">
                                {t("inscription.footer_note")}
                            </div>
                        </form>
                    </Reveal>
                </div>
                )}
            </Section>
        </div>
    );
}
